import React, { useState, useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import styles from './SkuTable.module.scss';
import { Thead } from '../Thead/Thead';
import { EmptyRow } from '../EmptyRow/EmptyRow';
import { money2 } from '../../utils';
import type { SkuLine } from '../../types';

interface SkuTableProps {
  rows: SkuLine[];
}

interface SkuGroup {
  code: string;
  description: string;
  lines: SkuLine[];
  minPrice: number;
  maxPrice: number;
  totalQty: number;
  locationCount: number;
}

// SKUs are shared across locations but priced per location, so rows
// are grouped by SKU code first. Each group header shows the code,
// description, how many locations carry it, and the price range
// across those locations. Expand a group to see the per-location
// breakdown: which PO, at which address, at what qty and price.
export function SkuTable({ rows }: SkuTableProps) {
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const groups = useMemo<SkuGroup[]>(() => {
    const map = new Map<
      string,
      { code: string; description: string; lines: SkuLine[] }
    >();
    for (const r of rows) {
      if (!map.has(r.code))
        map.set(r.code, {
          code: r.code,
          description: r.description,
          lines: [],
        });
      map.get(r.code)!.lines.push(r);
    }
    return Array.from(map.values()).map((g) => {
      const prices = g.lines.map((l) => l.unitPrice);
      const totalQty = g.lines.reduce((s, l) => s + l.qty, 0);
      const locationCount = new Set(g.lines.map((l) => l.location.id)).size;
      return {
        ...g,
        minPrice: Math.min(...prices),
        maxPrice: Math.max(...prices),
        totalQty,
        locationCount,
      };
    });
  }, [rows]);

  if (!groups.length) {
    return (
      <table className={styles.table}>
        <tbody>
          <EmptyRow label="No SKUs yet." span={6} />
        </tbody>
      </table>
    );
  }

  return (
    <table className={styles.table}>
      <Thead
        cols={[
          'SKU',
          'Description',
          'Locations',
          'Total qty',
          'Price range',
          '',
        ]}
      />
      <tbody>
        {groups.map((g) => {
          const isOpen = !!open[g.code];
          return (
            <React.Fragment key={g.code}>
              <tr
                className={styles.row}
                onClick={() => setOpen((o) => ({ ...o, [g.code]: !o[g.code] }))}
              >
                <td className={styles.code}>{g.code}</td>
                <td className={styles.text}>{g.description}</td>
                <td className={styles.text}>{g.locationCount}</td>
                <td className={styles.text}>{g.totalQty}</td>
                <td className={styles.priceRange}>
                  {g.minPrice === g.maxPrice
                    ? `$${g.minPrice.toFixed(2)}`
                    : `$${g.minPrice.toFixed(2)} – $${g.maxPrice.toFixed(2)}`}
                </td>
                <td className={styles.chevronCell}>
                  <ChevronDown
                    size={15}
                    className={
                      isOpen
                        ? `${styles.chevron} ${styles.chevronOpen}`
                        : styles.chevron
                    }
                  />
                </td>
              </tr>
              {isOpen && (
                <tr>
                  <td colSpan={6} className={styles.expandedCell}>
                    <table className={styles.subtable}>
                      <thead>
                        <tr className={styles.subHeadRow}>
                          <th className={`${styles.subTh} ${styles.first}`}>
                            Location
                          </th>
                          <th className={styles.subTh}>PO #</th>
                          <th className={styles.subTh}>Qty</th>
                          <th className={styles.subTh}>Unit price</th>
                          <th className={styles.subTh}>Line total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.lines.map((l) => (
                          <tr key={l.id} className={styles.subRow}>
                            <td className={`${styles.subText} ${styles.first}`}>
                              {l.location.name}
                            </td>
                            <td className={styles.subMonoMuted}>
                              {l.poNumber}
                            </td>
                            <td className={styles.subText}>{l.qty}</td>
                            <td className={styles.subMono}>
                              {money2(l.unitPrice)}
                            </td>
                            <td className={styles.subTotal}>
                              {money2(l.qty * l.unitPrice)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}
